<template>
	<div id="morningstar-ratings">
		<h3 class="module-title">
			Morningstar Rating<sup>TM</sup>
			<disclosure-superscript class="detail-page" anchor="morningstarRating"
				:statistics="['morningstarRating']" />
		</h3>
		<table>
			<tbody>
				<template v-for="(item, index) in getMstarRating.mstarRatingsArray" :key="index">
					<tr class="row-container">
						<th class="align-left">
							<div>{{ item.title }}<sup>TM</sup></div>
							<div v-if="index === 0">
								<div class="sub-info">
									Funds rated ({{ item.count }})
								</div>
							</div>
							<div v-else>
								<div class="sub-info">
									({{ item.count }} funds rated)
								</div>
							</div>
						</th>
						<th class="align-right">
							<div v-if="isValidRating(item.rating)" class="year-raiting-container">
								<img v-for="overallBlue in item.rating" :key="overallBlue.id"
									:style="{ 'background-image': `url(${getMstarRating.blueStarImg})` }" />
								<img v-for="overallGray in (5 - item.rating)" :key="overallGray.id"
									:style="{ 'background-image': `url(${getMstarRating.greyStarImg})` }" />
							</div>
							<div v-else>
								--
							</div>
						</th>
					</tr>
				</template>
				<tr class="row-container">
					<td class="align-left">
						<div>Overall Morningstar Risk</div>
					</td>
					<td class="align-right risk-raiting-row">
						<span v-if="isValidRating(getMstarRating.mStarRiskRatingOverall)">
							<span class="rating-text"> {{ getMstarRating.mStarRiskRatingOverallTxt }} </span>
							<div class="risk-raiting-container">
								<div v-for="(bluerisk, index) in getMstarRating.mStarRiskRatingOverall"
									:key="bluerisk.id" class="blue-box">
									<div v-if="index === 0">
										LOW
									</div>
									<div v-if="isBlueBoxHigh(index)">
										HIGH
									</div>
								</div>
								<div v-for="(greyrisk, index) in (5 - getMstarRating.mStarRiskRatingOverall)"
									:key="greyrisk.id" class="gray-box">
									<div v-if="isGrayBoxLow(index)">
										LOW
									</div>
									<div v-if="index === (4 - getMstarRating.mStarRiskRatingOverall)">
										HIGH
									</div>
								</div>
							</div>
						</span>
						<span v-else>--</span>
					</td>
				</tr>
				<tr class="row-container">
					<td class="align-left">
						Category
						<i-ball-modal tag="MorningstarCategory" />
					</td>
					<td class="align-right category-text">
						{{ getMstarRating.mStarCategory }}
					</td>
				</tr>
			</tbody>
		</table>
		<div class="link-container">
			<i class="link-arrow" />
			<a :href="`/${audience}/investments/morningstar-tools.html`">
				<div>ADVANTAGES AND DISADVANTAGES OF MORNINGSTAR RATINGS</div>
			</a>
		</div>
		<div class="as-of-date">
			Ratings are based on risk-adjusted returns as of {{ getMstarRating.mstarAsOfDate }} (updated monthly).
		</div>
	</div>
</template>

<script>
import { mapGetters } from 'vuex';

export default {
	name: 'MorningstarRatingModule',
	data() {
		return {
			title: 'Morningstar Rating'
		};
	},
	computed: {
		...mapGetters(['getMstarRating']),
		audience() {
			return this.getAudience == 'institutional' ? 'us' : this.getAudience;
		}
	},
	methods: {
		isBlueBoxHigh(index) {
			return index === 4 && this.getMstarRating.mStarRiskRatingOverall === 5;
		},
		isGrayBoxLow(index) {
			return index === 0 && this.getMstarRating.mStarRiskRatingOverall === 0;
		},
		isValidRating(val) {
			return typeof val === 'number';
		}
	},
	watch: {
		getMstarRating: {
			immediate: true,
			handler(val) {
				this.$EventBus.$emit('ExcelData', 'MorningstarRatingModule', {
					...val,
					mstarAsOfDate: `Ratings are based on risk-adjusted returns as of ${val.mstarAsOfDate} (updated monthly).`
				});
			}
		}
	}
};
</script>
<style lang="less" scoped>
@import '~_css_/variables';

#morningstar-ratings {
	.module-border-top();

	.as-of-date {
		margin-top: 20px;
		color: @gray;
		font-family: "AvenirNextLTW01-Condens 721299", Arial, sans-serif;
		font-size: 12px;
	}

	.align-right {
		text-align: right;
	}

	.align-left {
		text-align: left;
	}

	.link-container {
		display: flex;
		justify-content: space-around;

		a {
			font-family: "Avenir Next LT W01 Demi", Arial, sans-serif;
			flex: 1;
		}
	}

	.rating-text {
		padding-right: 5px;
		color: @dark-gray;
	}

	.category-text {
		color: @dark-gray;
	}

	.sub-info {
		padding: 0px;
	}

	td, th {
		color: @gray;
		font-size: 14px;
		font-family: AvenirNextLTW01-Regular, Arial, sans-serif;
		vertical-align: top;
		padding: 3px 8px 3px 0;

		&:first-of-type {
			width: 200px;
		}
	}

	.risk-raiting-row {
		margin: 3px 0;
	}

	.risk-raiting-container {
		display: flex;
		float: right;
		justify-content: space-between;
		width: 160px;

		div {
			color: #eeeeee;
			display: inline-block;
			font-family: "AvenirNextLTW01-Condens 721299", Arial, sans-serif;
			height: 20px;
			text-align: center;
			vertical-align: top;
			width: 30px;
		}

		.gray-box {
			background-color: #d4d4d4;
		}

		.blue-box {
			background-color: #009ADF;
		}
	}

	.row-container {
		height: 24px;
		.thin-light-gray-border-bottom();
	}

	table {
		table-layout: fixed;

		.com-mod-table-data {
			vertical-align: top;

			tr:last-of-type {
				border-bottom: none;
			}
		}
	}

	.year-raiting-container {
		display: flex;
		float: right;
		justify-content: space-between;
		width: 120px;

		img {
			background-repeat: no-repeat;
			background-position: center center;
			display: inline-block;
			margin-right: 5px;
			height: 16px;
			width: 16px;
		}
	}

	@media (max-width: 768px) {
		margin-bottom: 35px;

		.risk-raiting-row {
			display: table-cell;
			margin: 0;
		}

		.link-container {
			justify-content: flex-start;
		}
	}

	@media (max-width: 480px) {
		.risk-raiting-container {
			width: 110px;

			div {
				height: 12px;
				font-size: 10px;
				width: 20px;
			}
		}

		.link-container a {
			margin: 0px 0px 0px 5px
		}
	}
}
</style>
